/**
 * Clipboard utilities for chat message selection and copy/paste.
 *
 * Supports dual-format copy:
 * - `text/plain`: Transcript format for external paste (e.g., Slack)
 * - `text/html`: Contains embedded JSON payload for internal paste
 *
 * The Clipboard API only supports standard MIME types (text/plain, text/html, image/png),
 * so we embed our JSON data in an HTML element with a data attribute.
 *
 * Includes patient isolation validation to prevent cross-patient pasting.
 *
 * ## Data Flow for Relations (Embedded Quotes)
 *
 * When a message contains quoted content (via `relations`), here's how the data flows:
 *
 * 1. **API Response**: The `bffCrmPersonChat` endpoint returns `communications` with each
 *    chat message's `relations` array containing `content_object` entries of kind `chat`.
 *
 * 2. **Selection Hook**: `useMessageSelection` (in `useMessageSelection.ts`) receives
 *    `communications` and extracts relations in `getSelectedMessagesForClipboard()`:
 *    ```
 *    const quotedMessages = (msg.relations ?? [])
 *      .filter(r => r.content_object?.kind === 'chat')
 *      .map(r => ({ sender_name, sent_at, text, case_id }))
 *    ```
 *
 * 3. **Clipboard**: The `QuotedMessage.quoted_messages` array is populated from step 2
 *    and included in the clipboard payload.
 *
 * 4. **Transcript Rendering**: `formatAsTranscript()` iterates `quoted_messages` and
 *    renders them as indented blockquotes in the plain text output.
 *
 * Note: When quoting a single message via the MessageActions menu (not via selection),
 * `buildQuotedMessageFromCurrent()` does NOT have access to relations (they're not in
 * the Props type), so `quoted_messages` will be empty in that code path.
 */

import { asArrayOf, isFiniteNumber, isRecord, isStringValue, parseJson } from '~/shared/lib/guards'

/**
 * Data attribute name used to embed JSON in HTML clipboard data.
 */
const DATA_ATTRIBUTE = 'data-firefly-chat-selection'

/**
 * Embedded quoted message from a message's relations.
 * Contains the minimal info needed for transcript rendering.
 */
export interface EmbeddedTranscriptMessage {
  sender_name: string
  sent_at: string // ISO timestamp
  text: string
  /** Case ID the quoted message originated from */
  case_id: number | null
}

/**
 * Data stored in clipboard for each quoted message.
 * Contains everything needed to render the blockquote AND submit to API.
 */
export interface TranscriptMessage {
  /** Required for API submission */
  object_id: number
  content_type: 'chatmessagev2'

  /** Required for rendering blockquote */
  sender_name: string
  sent_at: string // ISO timestamp
  text: string

  /** For styling mine vs theirs bubbles */
  is_mine: boolean

  /** URL for sender avatar */
  avatar_url: string | null

  /** Attachment type when message has no text content */
  attachment_type: 'image' | 'file' | null

  /** Quoted messages embedded in this message (from relations) */
  quoted_messages: EmbeddedTranscriptMessage[]
}

/**
 * Complete clipboard payload including patient context for validation.
 */
interface ChatSelectionClipboardData {
  /** Person ID for patient isolation validation */
  person_id: number

  /** Message data */
  messages: TranscriptMessage[]

  /** ISO timestamp for debugging/staleness detection */
  copied_at: string
}

/**
 * Narrows a parsed clipboard payload, all or nothing.
 *
 * The isolation check below compares `person_id`, and every message is submitted to the API by
 * `object_id` and `content_type`, so those are the fields checked. The rest are display-only and a
 * missing one degrades to an empty bubble; a payload that fails this is not partially pasted.
 */
const asClipboardData = (value: unknown): ChatSelectionClipboardData | null => {
  if (!isRecord(value) || !isFiniteNumber(value.person_id) || !isStringValue(value.copied_at)) {
    return null
  }
  const messages = asArrayOf(
    value.messages,
    (message): message is TranscriptMessage =>
      isRecord(message) &&
      isFiniteNumber(message.object_id) &&
      message.content_type === 'chatmessagev2',
  )
  return messages ? { person_id: value.person_id, copied_at: value.copied_at, messages } : null
}

/**
 * Error thrown when attempting to paste messages from a different patient.
 */
export class PatientMismatchError extends Error {
  constructor() {
    super('Cannot paste messages from a different patient')
    this.name = 'PatientMismatchError'
  }
}

/**
 * Get the display text for a message, including attachment placeholders.
 * Text and attachments can coexist, so both are included when present.
 */
function getMessageDisplayText(msg: TranscriptMessage): string {
  const parts: string[] = []
  if (msg.text) {
    parts.push(msg.text)
  }
  if (msg.attachment_type === 'image') {
    parts.push('[image attachment]')
  }
  if (msg.attachment_type === 'file') {
    parts.push('[file attachment]')
  }
  return parts.join('\n')
}

/**
 * Format a timestamp as a UTC string.
 */
function formatTimestamp(sent_at: string): string {
  const date = new Date(sent_at)
  return date
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d{3}Z$/, ' UTC')
}

/**
 * Format messages as plain text transcript for external paste.
 *
 * Example output for regular messages:
 * ```
 * [Jane Doe 2026-03-25 10:30:00 UTC]
 * Hi, I have a question about my medication.
 *
 * [Dr. Smith 2026-03-25 10:32:00 UTC]
 * Of course, what would you like to know?
 * ```
 *
 * Example output for messages with quoted relations:
 * ```
 * [Dr. Smith 2026-03-25 10:32:00 UTC]
 * As they mentioned earlier...
 *     [Quoted from Case 123][Jane Doe 2026-03-25 10:30:00 UTC]
 *     Hi, I have a question about my medication.
 * ```
 */
function formatAsTranscript(messages: TranscriptMessage[]): string {
  return messages
    .map(msg => {
      const formattedDate = formatTimestamp(msg.sent_at)
      const displayText = getMessageDisplayText(msg)
      const lines: string[] = []

      // Parent message header and text
      lines.push(`[${msg.sender_name} ${formattedDate}]`)
      lines.push(displayText)

      // Embedded quoted messages (from relations)
      for (const quoted of msg.quoted_messages) {
        const quotedDate = formatTimestamp(quoted.sent_at)
        const quotedCasePrefix =
          quoted.case_id !== null ? `[Quoted from Case ${quoted.case_id}]` : ''
        lines.push(`    ${quotedCasePrefix}[${quoted.sender_name} ${quotedDate}]`)
        const quotedText = quoted.text || ''
        const indentedQuotedText = quotedText
          .split('\n')
          .map(quotedLine => `    ${quotedLine}`)
          .join('\n')
        lines.push(indentedQuotedText)
      }

      return lines.join('\n')
    })
    .join('\n\n')
}

/**
 * Copy messages to clipboard with support for multiple formats:
 * - Plain text transcript for external apps
 * - HTML with embedded JSON for internal paste with patient validation
 */
export async function copyMessagesToClipboard(
  personId: number,
  messages: TranscriptMessage[],
): Promise<void> {
  const payload: ChatSelectionClipboardData = {
    person_id: personId,
    messages,
    copied_at: new Date().toISOString(),
  }

  const transcript = formatAsTranscript(messages)

  // Embed JSON in a hidden span within the HTML. The HTML also renders nicely
  // when pasted into rich text editors.
  const encodedPayload = encodeURIComponent(JSON.stringify(payload))
  const newlineRegex = /\n/g
  const htmlTranscript = transcript.replace(newlineRegex, '<br>')
  const html = `<div ${DATA_ATTRIBUTE}="${encodedPayload}">${htmlTranscript}</div>`

  await navigator.clipboard.write([
    new ClipboardItem({
      'text/plain': new Blob([transcript], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' }),
    }),
  ])
}

/**
 * Read and validate clipboard data for a given patient.
 *
 * @param personId - The person ID of the current patient context
 * @returns Array of quoted messages if clipboard contains valid data, null otherwise
 * @throws PatientMismatchError if clipboard data is from a different patient
 */
export async function readClipboardForPerson(
  personId: number,
): Promise<TranscriptMessage[] | null> {
  const items = await navigator.clipboard.read()

  for (const item of items) {
    // Look for HTML content that contains our embedded data
    if (item.types.includes('text/html')) {
      const blob = await item.getType('text/html')
      const html = await blob.text()

      // Parse the HTML and look for our data attribute (I know)
      // https://stackoverflow.com/questions/1732348/regex-match-open-tags-except-xhtml-self-contained-tags
      const encodedPayload = html.match(new RegExp(`${DATA_ATTRIBUTE}="([^"]+)"`))?.[1]
      // No embedded data found
      if (!encodedPayload) continue

      const data = asClipboardData(parseJson(decodeURIComponent(encodedPayload)))
      // Anything on the clipboard carrying the data attribute reaches here, so a payload that does
      // not fit reads as no embedded data rather than as a payload from another patient.
      if (!data) continue

      // Patient isolation check
      if (data.person_id !== personId) {
        throw new PatientMismatchError()
      }

      return data.messages
    }
  }

  return null // No custom data found
}
